import React, { useEffect, useState } from 'react'
import {
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	SelectChangeEvent,
	FormControlProps,
	ListSubheader,
	FormHelperText,
	Checkbox,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

/**
 * Interface for individual options used in the CustomSelect component.
 * - @property {string} label - The display label for the option.
 * - @property {string} value - The underlying value for the option.
 */
export interface Options {
	label: string
	value: string
}

/**
 * Interface for option groups used in the CustomSelect component.
 * - @property {string} groupLabel - The label for the option group.
 * - @property {Options[]} options - Array of option objects within the group.
 */
export interface OptionsGroups {
	groupLabel: string
	options: Options[]
}

/**
 * Props for the CustomSelect component.
 * - @extends Omit<FormControlProps, 'error' | 'onChange'>
 * - @property {Options[] | undefined} options - Array of option objects with label and value.
 * - @property {OptionsGroups[] | undefined} optionsGroups - Array of option group objects.
 * - @property {string} label - Label for the select input.
 * - @property {boolean} [fullWidth=true] - If true, the select will take the full width of its container.
 * - @property {'small' | 'medium'} [size='medium'] - Size of the select input.
 * - @property {'outlined' | 'filled' | 'standard'} [variant='outlined'] - Variant of the select input.
 * - @property {boolean} [multiple=false] - If true, allows multiple selections.
 * - @property {{ state: boolean; message: string }} error - Error state and message for validation.
 * - @property {string | string[]} value - Current selected value(s). If multiple is true, this should be an array of strings.
 * - @property {(event: SelectChangeEvent) => void} [changeHandler] - Handler for change events.
 * - @property {(event: SelectChangeEvent) => void} [onChange] - Alternative handler for change events.
 * - @property {() => void} [onOpen] - Handler for when the select dropdown is opened.
 */
interface CustomSelectProps extends Omit<FormControlProps, 'error' | 'onChange'> {
	options?: Options[]
	optionsGroups?: OptionsGroups[]
	label: string
	value: string | string[]
	fullWidth?: boolean
	size?: 'small' | 'medium'
	variant?: 'outlined' | 'filled' | 'standard'
	multiple?: boolean
	withIndicators?: boolean
	error?: { state: boolean; message: string }
	changeHandler?: (event: SelectChangeEvent<string | string[]>) => void
	onChange?: (event: SelectChangeEvent<string | string[]>) => void
	onOpen?: () => void
}

/**
 * ## CustomSelect component using MUI Select with support for options, option groups, and error handling.
 * - @param {CustomSelectProps} props - Props for configuring the select component.
 * - @returns {JSX.Element} The rendered select component.
 */
const CustomSelect = ({
	options,
	optionsGroups,
	label,
	value,
	fullWidth = true,
	size = 'medium',
	variant = 'outlined',
	multiple = false,
	error = { state: false, message: '' },
	withIndicators = false,
	changeHandler,
	onChange,
	onOpen,
	...props
}: Readonly<CustomSelectProps>): React.JSX.Element => {
	// State management
	const [open, setOpen] = useState(false)
	const { sanitizedOptionsGroups, sanitizedOptions } = useSanitizeOptions(optionsGroups ?? [], options ?? [])

	/**
	 * Function to handle changes in selection.
	 * Calls the provided changeHandler or onChange prop if available.
	 * Closes the dropdown if not in multiple selection mode.
	 *
	 * @param {SelectChangeEvent} event - The change event from the select component.
	 */
	const handleChange = (event: SelectChangeEvent<string | string[]>) => {
		// Call external onChange if provided (support both prop names)
		if (changeHandler) {
			changeHandler(event)
		}
		if (onChange) {
			onChange(event)
		}
		// Close the dropdown after selection
		if (!multiple) {
			setOpen(false)
		}
	}

	/**
	 * Function to handle opening the select dropdown.
	 * Prevents opening if the select is disabled.
	 * Calls the provided onOpen prop if available.
	 */
	const handleOpen = () => {
		if (props.disabled) return
		if (onOpen) {
			onOpen()
		}
		setOpen(true)
	}

	// Generate unique ID for accessibility
	const labelId = `select-label-${sanitizeLabelForIdGeneration(label)}`

	return (
		<FormControl
			fullWidth={fullWidth}
			size={size}
			variant={variant}
			error={error.state}
			{...props}
			sx={{
				minWidth: '160px',
				'& .MuiPaper-root': {
					boxShadow:
						'0 3px 14px 2px rgba(9, 32, 86, 0.12), 0 8px 10px 1px rgba(9, 32, 86, 0.14), 0 5px 5px -3px rgba(9, 32, 86, 0.20)',
				},
				...props.sx,
			}}
		>
			<InputLabel
				id={labelId}
				sx={{ top: multiple ? '-4px' : '2px', '&.MuiInputLabel-shrink': { top: 0 } }}
			>
				{label}
			</InputLabel>
			{multiple ? (
				<MultipleSelect
					labelId={labelId}
					open={open}
					value={typeof value === 'string' ? value.split(', ').filter(Boolean) : value}
					label={label}
					handleOpen={handleOpen}
					setOpen={setOpen}
					optionsGroups={sanitizedOptionsGroups}
					options={sanitizedOptions}
					handleChange={handleChange}
					withIndicators={withIndicators}
				/>
			) : (
				<SingleSelect
					labelId={labelId}
					open={open}
					value={typeof value === 'string' ? value : ''}
					label={label}
					handleOpen={handleOpen}
					setOpen={setOpen}
					optionsGroups={sanitizedOptionsGroups}
					options={sanitizedOptions}
					handleChange={handleChange}
				/>
			)}
			{error.state && <FormHelperText sx={{ fontFamily: 'var(--font-family)' }}>Erro - {error.message}</FormHelperText>}
		</FormControl>
	)
}

export default CustomSelect

interface SingleSelectProps {
	labelId: string
	open: boolean
	label: string
	value: string
	handleOpen: () => void
	setOpen: (open: boolean) => void
	handleChange: (event: SelectChangeEvent) => void
	optionsGroups?: OptionsGroups[]
	options?: Options[]
}
const SingleSelect = ({
	labelId,
	open,
	label,
	handleOpen,
	setOpen,
	handleChange,
	value,
	optionsGroups,
	options,
}: Readonly<SingleSelectProps>) => {
	const [inputValue, setInputValue] = useState(value)

	const handleValueChange = (event: SelectChangeEvent) => {
		setInputValue(event.target.value)
		handleChange(event)
	}

	return (
		<Select
			IconComponent={() => (
				// NOSONAR  - The documentation recomends this pattern (https://mui.com/material-ui/api/select/#select-prop-IconComponent)
				<KeyboardArrowDownIcon
					sx={{
						color: '#00A5FF',
						marginRight: '0.5rem',
						transition: '0.3s ease',
						transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
					}}
				/>
			)}
			labelId={labelId}
			value={inputValue}
			label={label}
			onChange={handleValueChange}
			onOpen={handleOpen}
			onClose={() => setOpen(false)}
			open={open}
			sx={{
				width: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				textAlign: 'left',
				fontFamily: 'var(--font-family)',
			}}
		>
			{optionsGroups &&
				optionsGroups.length > 0 &&
				optionsGroups.map((group, idx) => (
					<React.Fragment key={group.groupLabel + idx}>
						<ListSubheader
							role='optgroup'
							aria-labelledby={`select-label-${labelId}`}
							sx={{ fontFamily: 'var(--font-family)' }}
						>
							{group.groupLabel}
						</ListSubheader>
						{group.options.map((option, idx) => (
							<MenuItem // NOSONAR  - The documentation recomends this pattern (https://mui.com/material-ui/react-select/#basic-select)
								role='option'
								key={option.value + idx}
								value={option.value}
								sx={{ fontFamily: 'var(--font-family)', '&.Mui-selected': { backgroundColor: '#fff' } }}
							>
								{option.label}
							</MenuItem>
						))}
					</React.Fragment>
				))}
			{options &&
				options.length > 0 &&
				options.map((option, idx) => (
					<MenuItem // NOSONAR  - The documentation recomends this pattern (https://mui.com/material-ui/react-select/#basic-select)
						role='option'
						aria-labelledby={`select-label-${labelId}`}
						key={option.value + idx}
						value={option.value}
						sx={{
							fontFamily: 'var(--font-family)',
							'&.Mui-selected': { backgroundColor: '#fff' },
						}}
					>
						{option.label}
					</MenuItem>
				))}
		</Select>
	)
}

interface MultipleSelectProps {
	labelId: string
	open: boolean
	label: string
	value: string[]
	withIndicators?: boolean
	handleOpen: () => void
	setOpen: (open: boolean) => void
	handleChange: (event: SelectChangeEvent<string[]>) => void
	optionsGroups?: OptionsGroups[]
	options?: Options[]
}

const MultipleSelect = ({
	labelId,
	open,
	label,
	value,
	handleOpen,
	setOpen,
	handleChange,
	optionsGroups,
	withIndicators = false,
	options,
}: Readonly<MultipleSelectProps>) => {
	const [values, setValues] = useState<string[]>([])

	useEffect(() => {
		setValues(value)
	}, [value]) // Sync internal state with prop changes

	// useEffect(() => console.log('values changed:', values), [values]) // Debugging useEffect to track values changes

	const handleValuesChange = (event: SelectChangeEvent<string[]>) => {
		const nextValues =
			typeof event.target.value === 'string' ? event.target.value.split(',').map(v => v.trim()) : event.target.value

		setValues(nextValues)
		handleChange(event)
	}

	const getLabelByValue = (selectedValue: string): string => {
		const groupedOption = optionsGroups?.flatMap(group => group.options).find(option => option.value === selectedValue)
		if (groupedOption) {
			return groupedOption.label
		}

		const flatOption = options?.find(option => option.value === selectedValue)
		return flatOption?.label ?? selectedValue
	}

	return (
		<Select
			IconComponent={() => (
				// NOSONAR  - The documentation recomends this pattern (https://mui.com/material-ui/api/select/#select-prop-IconComponent)
				<KeyboardArrowDownIcon
					sx={{
						color: '#00A5FF',
						marginRight: '0.5rem',
						transition: '0.3s ease',
						transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
					}}
				/>
			)}
			labelId={labelId}
			multiple
			value={values}
			renderValue={value => (value as string[]).map(getLabelByValue).join(', ')}
			label={label}
			onChange={handleValuesChange}
			onOpen={handleOpen}
			onClose={() => setOpen(false)}
			open={open}
			sx={{
				width: '100%',
				height: '40px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				textAlign: 'left',
				fontFamily: 'var(--font-family)',
				fontSize: '13px',
				fontWeight: 500,
				lineHeight: '130%',
			}}
		>
			{optionsGroups &&
				optionsGroups.length > 0 &&
				optionsGroups.map((group, idx) => (
					<React.Fragment key={group.groupLabel + idx}>
						<ListSubheader
							role='optgroup'
							aria-labelledby={labelId}
							sx={{ fontFamily: 'var(--font-family)', fontSize: '13px', fontWeight: 500, lineHeight: '130%' }}
						>
							{group.groupLabel}
						</ListSubheader>
						{group.options.map(option => (
							<MenuItem // NOSONAR
								key={option.value}
								role='option'
								value={option.value}
								sx={{
									fontFamily: 'var(--font-family)',
									fontSize: '12px',
									fontWeight: 500,
									lineHeight: '150%',
									display: 'flex',
									alignItems: 'center',
									gap: '11.5px',
									'&.Mui-selected, &.Mui-selected:hover': { backgroundColor: '#fff' },
								}}
							>
								{withIndicators && (
									<Checkbox
										checked={values.includes(option.value)}
										tabIndex={-1}
										disableRipple
										inputProps={{ 'aria-hidden': true }}
										sx={{
											pointerEvents: 'none',
											width: '15px',
											height: '15px',
											color: 'var(--primary-light)',
											'&.Mui-checked': {
												color: 'var(--primary)',
											},
										}}
									/>
								)}
								{option.label}
							</MenuItem>
						))}
					</React.Fragment>
				))}
			{options &&
				options.length > 0 &&
				options.map(option => (
					<MenuItem // NOSONAR
						key={option.value}
						role='option'
						value={option.value}
						sx={{
							fontFamily: 'var(--font-family)',
							fontSize: '12px',
							fontWeight: 500,
							lineHeight: '150%',
							display: 'flex',
							alignItems: 'center',
							gap: '11.5px',
							'&.Mui-selected, &.Mui-selected:hover': { backgroundColor: '#fff' },
						}}
					>
						{withIndicators && (
							<Checkbox
								checked={values.includes(option.value)}
								tabIndex={-1}
								disableRipple
								inputProps={{ 'aria-hidden': true }}
								sx={{
									pointerEvents: 'none',
									width: '15px',
									height: '15px',
									color: 'var(--primary-light)',
									'&.Mui-checked': {
										color: 'var(--primary)',
									},
								}}
							/>
						)}
						{option.label}
					</MenuItem>
				))}
		</Select>
	)
}

// Custom Hook 'useSanitizeOptions' to sanitize options and option groups
// This hook can be used to encapsulate the sanitization logic
// and can be imported and used in other components if needed.
export const useSanitizeOptions = (optionsGroups: OptionsGroups[], options: Options[]) => {
	const sanitizeOptionsGroups = (optionsGroups: OptionsGroups[]): OptionsGroups[] =>
		optionsGroups
			?.filter(group => group.options.length > 0) // Remove empty groups
			// Remove invalid options within groups
			.map(group => ({
				...group,
				options: group.options.filter(
					option => option.label && option.label.length > 0 && option.value && option.value.length > 0,
				),
			}))

	const sanitizeOptions = (options: Options[]): Options[] =>
		// Remove invalid options
		options.filter(option => option.label && option.label.length > 0 && option.value && option.value.length > 0)

	const sanitizedOptionsGroups = sanitizeOptionsGroups(optionsGroups)
	const sanitizedOptions = sanitizeOptions(options)

	return { sanitizedOptionsGroups, sanitizedOptions }
}

const sanitizeLabelForIdGeneration = (label: string) => label.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase() // Remove special characters and convert to lowercase
